import { useState, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import SEO from '@/components/feature/SEO';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import VideoCard from '@/components/base/VideoCard';
import { categories, getVideosByCategory } from '@/mocks/videos';

const categoryMeta: Record<string, { title: string; description: string; keywords: string }> = {
  all: {
    title: '全部视频',
    description: '浏览全部蘑菇成品人视频教程，涵盖蘑菇种植全流程、成品展示、人工培育、制作技巧和采收技术等全方位蘑菇种植视频资源。',
    keywords: '蘑菇成品人视频,蘑菇种植视频教程,蘑菇成品展示视频,蘑菇人工培育,蘑菇采收技术',
  },
  planting: {
    title: '种植全流程',
    description: '蘑菇种植全流程视频教程，从菌种培育到出菇管理，完整展示蘑菇种植的每一个环节，适合新手和专业种植者学习蘑菇种植技术。',
    keywords: '蘑菇种植全流程,蘑菇种植视频教程,蘑菇培育过程,菌种制作视频',
  },
  display: {
    title: '成品展示',
    description: '蘑菇成品展示视频精选，展示高品质蘑菇成品的外观、品质特征和分级标准，帮助了解优质蘑菇成品的评判标准。',
    keywords: '蘑菇成品展示,蘑菇品质展示视频,蘑菇成品欣赏,优质蘑菇展示',
  },
  cultivation: {
    title: '人工培育',
    description: '蘑菇人工培育全流程视频教程，详细讲解人工操作技巧，包括菌种分离、接种操作、培养管理等关键技术。',
    keywords: '蘑菇人工培育,蘑菇种植操作视频,人工培养蘑菇,蘑菇种植技术',
  },
  making: {
    title: '制作技巧',
    description: '蘑菇成品制作视频教程，涵盖蘑菇干制品加工、蘑菇酱料制作、蘑菇深加工等实用制作技巧。',
    keywords: '蘑菇成品制作,蘑菇加工视频,蘑菇制作教程,蘑菇深加工技术',
  },
  harvest: {
    title: '采收技术',
    description: '蘑菇采收技术视频教程，讲解不同菇类的最佳采收时机、采收方法和采后处理技术，确保蘑菇成品的优良品质。',
    keywords: '蘑菇成品采收,蘑菇采收技术,蘑菇采摘视频,蘑菇采后处理',
  },
};

export default function CategoryPage() {
  const { slug = 'all' } = useParams<{ slug: string }>();
  const [activeSlug, setActiveSlug] = useState(slug);
  const [sortBy, setSortBy] = useState<'latest' | 'popular'>('latest');

  const currentCategory = categories.find((c) => c.slug === activeSlug) || categories[0];
  const meta = categoryMeta[activeSlug] || categoryMeta.all;

  const videos = useMemo(() => {
    const filtered = getVideosByCategory(activeSlug);
    if (sortBy === 'popular') {
      return [...filtered].sort((a, b) => b.views - a.views);
    }
    return [...filtered].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
  }, [activeSlug, sortBy]);

  const categoryFilters = categories.filter((c) => c.slug !== 'all');
  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: activeSlug === 'all' ? '全部视频' : currentCategory.name },
  ];

  return (
    <>
      <SEO
        title={meta.title}
        description={meta.description}
        keywords={meta.keywords}
        canonicalUrl={`https://mushroom-tutorial.com/category/${activeSlug}`}
        schemaJson={{
          '@context': 'https://schema.org',
          '@type': 'CollectionPage',
          name: `蘑菇成品人视频 - ${currentCategory.name}`,
          description: meta.description,
          url: `https://mushroom-tutorial.com/category/${activeSlug}`,
        }}
      />
      <Navbar />
      <main className="pt-16">
        <div className="bg-background-100 border-b border-background-200/70">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <Breadcrumb items={breadcrumbItems} />
            <div className="pb-6">
              <h1 className="font-heading text-2xl md:text-3xl text-foreground-900 mb-2">
                蘑菇成品人视频 - {currentCategory.name}
              </h1>
              <p className="text-sm text-foreground-500 max-w-2xl">{currentCategory.description}</p>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 md:px-6 py-6">
          <div className="flex flex-col lg:flex-row gap-6">
            <aside className="lg:w-60 shrink-0">
              <div className="lg:sticky lg:top-20">
                <h3 className="font-heading text-sm text-foreground-900 mb-3">视频分类</h3>
                <nav className="space-y-1">
                  <button
                    onClick={() => setActiveSlug('all')}
                    className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${
                      activeSlug === 'all'
                        ? 'bg-primary-50 text-primary-600 font-medium'
                        : 'text-foreground-600 hover:bg-background-100'
                    }`}
                  >
                    <i className="ri-play-list-2-line mr-2"></i>
                    全部视频
                  </button>
                  {categoryFilters.map((cat) => (
                    <button
                      key={cat.slug}
                      onClick={() => setActiveSlug(cat.slug)}
                      className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${
                        activeSlug === cat.slug
                          ? 'bg-primary-50 text-primary-600 font-medium'
                          : 'text-foreground-600 hover:bg-background-100'
                      }`}
                    >
                      <i className={`${cat.icon} mr-2`}></i>
                      {cat.name}
                      <span className="text-xs text-foreground-400 ml-1">({cat.count})</span>
                    </button>
                  ))}
                </nav>
              </div>
            </aside>

            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between mb-5">
                <p className="text-sm text-foreground-500">
                  共 <strong className="text-foreground-800">{videos.length}</strong> 个蘑菇成品人视频
                </p>
                <div className="flex items-center gap-1 bg-background-100 rounded-lg p-0.5">
                  <button
                    onClick={() => setSortBy('latest')}
                    className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                      sortBy === 'latest' ? 'bg-background-50 text-foreground-900' : 'text-foreground-500 hover:text-foreground-700'
                    }`}
                  >
                    最新
                  </button>
                  <button
                    onClick={() => setSortBy('popular')}
                    className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                      sortBy === 'popular' ? 'bg-background-50 text-foreground-900' : 'text-foreground-500 hover:text-foreground-700'
                    }`}
                  >
                    最热
                  </button>
                </div>
              </div>

              {videos.length === 0 ? (
                <div className="text-center py-20">
                  <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                    <i className="ri-video-off-line text-4xl text-foreground-300"></i>
                  </div>
                  <p className="text-foreground-500">暂无该分类的蘑菇成品人视频</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5">
                  {videos.map((video, idx) => (
                    <div
                      key={video.id}
                      className="animate-fade-in-up"
                      style={{ animationDelay: `${idx * 60}ms` }}
                    >
                      <VideoCard video={video} />
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}